import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Loader2, Merge } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type {
  CampoDaPreviaResponse,
  CandidatoAMesclagemResponse,
  MesclarPacientesDtoEscolhas,
  PacienteResponse,
  PreviaDeMesclagemResponse,
} from '@/api/generated/model';
import { getExamesControllerListarQueryKey } from '@/api/generated/exames/exames';
import { getPacientesControllerListarQueryKey } from '@/api/generated/pacientes/pacientes';
import {
  getMesclagemDePacientesControllerListarQueryKey,
  useMesclagemDePacientesControllerCandidatos,
  useMesclagemDePacientesControllerMesclar,
  useMesclagemDePacientesControllerPrevia,
} from '@/api/generated/pacientes-mesclagens/pacientes-mesclagens';
import { ApiError } from '@/api/http-client';
import { SEXOS } from '@/features/pacientes/lib/especies';
import { resumirTotais } from '@/features/pacientes/lib/mesclagem';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

type Escolha = 'MANTIDO' | 'ABSORVIDO' | 'AMBOS';
type Campo = keyof MesclarPacientesDtoEscolhas;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** O paciente de onde a ação partiu. */
  paciente: PacienteResponse;
}

const MOTIVOS: Record<string, string> = { NOME: 'Nome parecido', TUTOR: 'Mesmo tutor' };

function dataCurta(iso: string | null): string | null {
  if (!iso) return null;
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

/**
 * Juntar dois pacientes que são o mesmo animal (duplicado do sync do Vertis).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Três passos:
 *   1. Escolher o outro paciente — só aparecem os que o servidor aceita
 *      (mesma espécie e nome parecido ou mesmo tutor) — e qual dos dois FICA.
 *   2. Comparar o cadastro campo a campo. O que só um tem completa o outro
 *      sozinho; o que os dois têm diferente o usuário escolhe (obrigatório).
 *   3. Revisar o resultado e confirmar.
 *
 *  O paciente que não fica é arquivado e aponta para o que ficou: os próximos
 *  exames do laboratório com os dados dele caem no paciente que ficou. Dá para
 *  desfazer pelo "Histórico de junções".
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function MesclarPacientesDialogo({ aberto, aoFechar, paciente }: Props) {
  const queryClient = useQueryClient();

  const [passo, setPasso] = useState<1 | 2 | 3>(1);
  const [outro, setOutro] = useState<CandidatoAMesclagemResponse | null>(null);
  const [ficaId, setFicaId] = useState<string>(paciente.id);
  const [filtro, setFiltro] = useState('');
  const [escolhas, setEscolhas] = useState<Partial<Record<Campo, Escolha>>>({});
  const [erroDoServidor, setErroDoServidor] = useState<string | null>(null);

  useEffect(() => {
    if (aberto) return;
    setPasso(1);
    setOutro(null);
    setFicaId(paciente.id);
    setFiltro('');
    setEscolhas({});
    setErroDoServidor(null);
  }, [aberto, paciente.id]);

  const candidatos = useMesclagemDePacientesControllerCandidatos(paciente.id, {
    query: { enabled: aberto },
  });

  const mantidoId = ficaId;
  const absorvidoId = outro ? (ficaId === paciente.id ? outro.id : paciente.id) : '';

  const previa = useMesclagemDePacientesControllerPrevia(
    { mantidoId, absorvidoId },
    { query: { enabled: aberto && passo >= 2 && Boolean(outro) } },
  );

  const mesclar = useMesclagemDePacientesControllerMesclar();

  const itensFiltrados = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    const itens = candidatos.data?.itens ?? [];
    if (!termo) return itens;
    return itens.filter((c) =>
      [c.nome, c.tutorNome, c.raca].some((v) => v?.toLowerCase().includes(termo)),
    );
  }, [candidatos.data, filtro]);

  const conflitos = (previa.data?.campos ?? []).filter((c) => c.status === 'CONFLITO');
  const pendentes = conflitos.filter((c) => !escolhas[c.campo as Campo]);

  function escolherOutro(candidato: CandidatoAMesclagemResponse) {
    setOutro(candidato);
    setEscolhas({});
    // Sugestão de quem fica: quem tem mais exames; empate, o mais antigo — é
    // o cadastro que mais gente já conhece.
    const outroGanha =
      candidato.totalExames > paciente.totalExames ||
      (candidato.totalExames === paciente.totalExames && candidato.criadoEm < paciente.criadoEm);
    setFicaId(outroGanha ? candidato.id : paciente.id);
  }

  async function confirmar() {
    if (!outro) return;
    setErroDoServidor(null);
    try {
      const resultado = await mesclar.mutateAsync({
        data: { mantidoId, absorvidoId, escolhas },
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getPacientesControllerListarQueryKey() }),
        queryClient.invalidateQueries({
          queryKey: getMesclagemDePacientesControllerListarQueryKey(),
        }),
        queryClient.invalidateQueries({ queryKey: getExamesControllerListarQueryKey() }),
      ]);
      toast.success(`"${resultado.absorvido.nome}" foi juntado em "${resultado.mantido.nome}".`);
      aoFechar();
    } catch (erro) {
      setErroDoServidor(
        erro instanceof ApiError ? erro.message : 'Não foi possível juntar os pacientes.',
      );
    }
  }

  const nomeDoQueFica = mantidoId === paciente.id ? paciente.nome : outro?.nome;
  const nomeDoQueSai = mantidoId === paciente.id ? outro?.nome : paciente.nome;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="w-5 h-5 text-brand" />
            Juntar &quot;{paciente.nome}&quot; com outro paciente
          </DialogTitle>
          <DialogDescription>
            Passo {passo} de 3 —{' '}
            {passo === 1
              ? 'escolha o paciente duplicado e qual dos dois fica.'
              : passo === 2
                ? 'escolha de qual paciente vem cada informação diferente.'
                : 'confira o resultado antes de juntar.'}
          </DialogDescription>
        </DialogHeader>

        {erroDoServidor && (
          <Alert variant="destructive">
            <AlertDescription>{erroDoServidor}</AlertDescription>
          </Alert>
        )}

        {passo === 1 && (
          <div className="space-y-4">
            {candidatos.isPending ? (
              <EstadoCarregando rotulo="Procurando pacientes parecidos..." />
            ) : candidatos.isError ? (
              <EstadoErro aoTentarDeNovo={() => void candidatos.refetch()} />
            ) : (candidatos.data?.itens.length ?? 0) === 0 ? (
              <EstadoVazio
                icone={Merge}
                titulo="Nenhum paciente parecido"
                descricao="Só dá para juntar pacientes da mesma espécie com nome parecido ou com o mesmo tutor."
              />
            ) : (
              <>
                <Input
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="Filtrar por nome, tutor ou raça..."
                  aria-label="Filtrar candidatos"
                />
                <div role="radiogroup" aria-label="Paciente duplicado" className="space-y-2">
                  {itensFiltrados.map((c) => {
                    const selecionado = outro?.id === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        role="radio"
                        aria-checked={selecionado}
                        onClick={() => escolherOutro(c)}
                        className={`w-full text-left rounded-lg border-2 p-3 transition-colors ${
                          selecionado
                            ? 'border-brand bg-brand/5'
                            : 'border-border hover:border-brand/40'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-foreground">{c.nome}</p>
                            <p className="text-xs text-muted-foreground">
                              {[
                                c.tutorNome && `Tutor: ${c.tutorNome}`,
                                c.raca,
                                c.dataNascimento && `Nasc. ${dataCurta(c.dataNascimento)}`,
                                `Vet.: ${c.veterinarioNome}`,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {c.totalExames} exames · {c.totalProntuarios} prontuários · cadastrado
                              em {dataCurta(c.criadoEm)}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-1 justify-end shrink-0">
                            {c.motivos.map((m) => (
                              <Badge key={m} variant="outline" className="text-xs font-normal">
                                {MOTIVOS[m] ?? m}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {itensFiltrados.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nenhum candidato com esse filtro.
                    </p>
                  )}
                </div>

                {outro && (
                  <fieldset className="rounded-lg border border-border p-3 space-y-2">
                    <legend className="text-sm font-medium px-1">Qual paciente fica?</legend>
                    <p className="text-xs text-muted-foreground">
                      O outro é arquivado, e os exames, prontuários e demais registros dele passam
                      para o que fica.
                    </p>
                    {[
                      { id: paciente.id, nome: paciente.nome, exames: paciente.totalExames },
                      { id: outro.id, nome: outro.nome, exames: outro.totalExames },
                    ].map((opcao) => (
                      <label
                        key={opcao.id}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="fica"
                          checked={ficaId === opcao.id}
                          onChange={() => {
                            setFicaId(opcao.id);
                            setEscolhas({});
                          }}
                        />
                        <span className="font-medium">{opcao.nome}</span>
                        <span className="text-muted-foreground">({opcao.exames} exames)</span>
                      </label>
                    ))}
                  </fieldset>
                )}
              </>
            )}
          </div>
        )}

        {passo === 2 && (
          <PassoDeComparacao
            previa={previa.data}
            carregando={previa.isPending}
            erro={previa.isError}
            mensagemDeErro={previa.error instanceof ApiError ? previa.error.message : null}
            aoTentarDeNovo={() => void previa.refetch()}
            escolhas={escolhas}
            aoEscolher={(campo, escolha) =>
              setEscolhas((atual) => ({ ...atual, [campo]: escolha }))
            }
          />
        )}

        {passo === 3 && previa.data && <PassoDeRevisao previa={previa.data} escolhas={escolhas} />}

        <DialogFooter className="gap-2">
          {passo > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setPasso((p) => (p === 3 ? 2 : 1))}
              disabled={mesclar.isPending}
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
          )}
          <Button type="button" variant="outline" onClick={aoFechar} disabled={mesclar.isPending}>
            Cancelar
          </Button>

          {passo === 1 && (
            <Button type="button" onClick={() => setPasso(2)} disabled={!outro}>
              Comparar
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
          {passo === 2 && (
            <Button
              type="button"
              onClick={() => setPasso(3)}
              disabled={!previa.data || pendentes.length > 0}
              title={
                pendentes.length > 0 ? 'Escolha um valor para cada campo diferente' : undefined
              }
            >
              {pendentes.length > 0 ? `Faltam ${pendentes.length} escolha(s)` : 'Revisar'}
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
          {passo === 3 && (
            <Button type="button" onClick={() => void confirmar()} disabled={mesclar.isPending}>
              {mesclar.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Merge className="w-4 h-4" />
              )}
              Juntar em &quot;{nomeDoQueFica}&quot;
            </Button>
          )}
        </DialogFooter>

        {passo === 3 && (
          <p className="text-xs text-muted-foreground">
            &quot;{nomeDoQueSai}&quot; será arquivado. A junção pode ser desfeita pelo Histórico de
            junções, na tela de Pacientes.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Passo 2 ──────────────────────────────────────────────────────────────────

function PassoDeComparacao({
  previa,
  carregando,
  erro,
  mensagemDeErro,
  aoTentarDeNovo,
  escolhas,
  aoEscolher,
}: {
  previa: PreviaDeMesclagemResponse | undefined;
  carregando: boolean;
  erro: boolean;
  mensagemDeErro: string | null;
  aoTentarDeNovo: () => void;
  escolhas: Partial<Record<Campo, Escolha>>;
  aoEscolher: (campo: Campo, escolha: Escolha) => void;
}) {
  if (carregando) return <EstadoCarregando rotulo="Comparando os cadastros..." />;
  if (erro || !previa) {
    return mensagemDeErro ? (
      <Alert variant="destructive">
        <AlertDescription>{mensagemDeErro}</AlertDescription>
      </Alert>
    ) : (
      <EstadoErro aoTentarDeNovo={aoTentarDeNovo} />
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium w-40">Campo</th>
              <th className="px-3 py-2 font-medium">
                {previa.mantido.nome} <span className="text-xs text-brand">(fica)</span>
              </th>
              <th className="px-3 py-2 font-medium">
                {previa.absorvido.nome}{' '}
                <span className="text-xs text-muted-foreground">(será arquivado)</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {previa.campos.map((campo) => (
              <LinhaDeCampo
                key={campo.campo}
                campo={campo}
                previa={previa}
                escolha={escolhas[campo.campo as Campo]}
                aoEscolher={(e) => aoEscolher(campo.campo as Campo, e)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Campos que só um dos pacientes tem são completados automaticamente.
      </p>
    </div>
  );
}

function Valor({
  campo,
  texto,
  previa,
  lado,
}: {
  campo: CampoDaPreviaResponse;
  texto: string | null;
  previa: PreviaDeMesclagemResponse;
  lado: 'mantido' | 'absorvido';
}) {
  if (campo.campo === 'fotoArquivoId') {
    const url = previa[lado].fotoUrl;
    return url ? (
      <img
        src={url}
        alt={`Foto de ${previa[lado].nome}`}
        className="w-12 h-12 rounded object-cover"
      />
    ) : (
      <span className="text-muted-foreground">—</span>
    );
  }
  if (!texto) return <span className="text-muted-foreground">—</span>;
  return <span className="whitespace-pre-line break-words">{texto}</span>;
}

function LinhaDeCampo({
  campo,
  previa,
  escolha,
  aoEscolher,
}: {
  campo: CampoDaPreviaResponse;
  previa: PreviaDeMesclagemResponse;
  escolha: Escolha | undefined;
  aoEscolher: (escolha: Escolha) => void;
}) {
  const conflito = campo.status === 'CONFLITO';
  // Sem conflito, o lado que vale é o preenchido (ou o mantido, se iguais).
  // (O texto vazio do servidor é o "não preenchido": inclusive foto e sexo
  // indefinido.)
  const automatico: 'mantido' | 'absorvido' =
    campo.status === 'SO_UM_PREENCHIDO' && !campo.textoMantido ? 'absorvido' : 'mantido';

  function celula(lado: 'mantido' | 'absorvido') {
    const texto = lado === 'mantido' ? campo.textoMantido : campo.textoAbsorvido;
    const valorEscolha: Escolha = lado === 'mantido' ? 'MANTIDO' : 'ABSORVIDO';

    if (!conflito) {
      const vale = campo.status === 'IGUAL' ? lado === 'mantido' : automatico === lado;
      return (
        <td className={`px-3 py-2 align-top ${vale ? '' : 'text-muted-foreground'}`}>
          <Valor campo={campo} texto={texto} previa={previa} lado={lado} />
        </td>
      );
    }

    const marcado = escolha === valorEscolha;
    return (
      <td className="px-2 py-1.5 align-top">
        <button
          type="button"
          role="radio"
          aria-checked={marcado}
          aria-label={`${campo.rotulo}: usar o valor de ${previa[lado].nome}`}
          onClick={() => aoEscolher(valorEscolha)}
          className={`w-full text-left rounded-md border-2 px-2 py-1.5 flex items-start gap-2 transition-colors ${
            marcado ? 'border-brand bg-brand/5' : 'border-amber-500/40 hover:border-brand/40'
          }`}
        >
          <span
            className={`mt-0.5 w-4 h-4 rounded-full border shrink-0 flex items-center justify-center ${
              marcado ? 'border-brand bg-brand text-brand-foreground' : 'border-muted-foreground'
            }`}
          >
            {marcado && <Check className="w-3 h-3" />}
          </span>
          <Valor campo={campo} texto={texto} previa={previa} lado={lado} />
        </button>
      </td>
    );
  }

  return (
    <tr className={`border-t border-border ${conflito ? 'bg-amber-500/5' : ''}`}>
      <th scope="row" className="px-3 py-2 text-left font-medium align-top">
        <div className="flex flex-col gap-1">
          {campo.rotulo}
          {conflito ? (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-normal">
              <AlertTriangle className="w-3 h-3" />
              Escolha um
            </span>
          ) : campo.status === 'SO_UM_PREENCHIDO' ? (
            <span className="text-xs text-muted-foreground font-normal">
              Completado automaticamente
            </span>
          ) : null}
          {conflito && campo.aceitaAmbos && (
            <label className="flex items-center gap-1.5 text-xs font-normal cursor-pointer">
              <input
                type="radio"
                checked={escolha === 'AMBOS'}
                onChange={() => aoEscolher('AMBOS')}
                aria-label={`${campo.rotulo}: juntar os dois textos`}
              />
              Juntar os dois
            </label>
          )}
        </div>
      </th>
      {celula('mantido')}
      {celula('absorvido')}
    </tr>
  );
}

// ── Passo 3 ──────────────────────────────────────────────────────────────────

function PassoDeRevisao({
  previa,
  escolhas,
}: {
  previa: PreviaDeMesclagemResponse;
  escolhas: Partial<Record<Campo, Escolha>>;
}) {
  function valorFinal(campo: CampoDaPreviaResponse): string | null {
    const escolha = escolhas[campo.campo as Campo];
    if (campo.status === 'CONFLITO') {
      if (escolha === 'AMBOS')
        return `${campo.textoMantido ?? ''}\n\n${campo.textoAbsorvido ?? ''}`;
      return escolha === 'ABSORVIDO' ? campo.textoAbsorvido : campo.textoMantido;
    }
    return campo.textoMantido ?? campo.textoAbsorvido;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border divide-y divide-border">
        {previa.campos.map((campo) => {
          const texto = valorFinal(campo);
          const deOndeVem =
            campo.status === 'CONFLITO'
              ? escolhas[campo.campo as Campo] === 'AMBOS'
                ? 'dos dois'
                : escolhas[campo.campo as Campo] === 'ABSORVIDO'
                  ? `de ${previa.absorvido.nome}`
                  : `de ${previa.mantido.nome}`
              : null;
          return (
            <div key={campo.campo} className="flex gap-3 px-3 py-2 text-sm">
              <span className="w-40 shrink-0 font-medium">{campo.rotulo}</span>
              <span className="flex-1 whitespace-pre-line break-words">
                {campo.campo === 'fotoArquivoId'
                  ? (texto ?? '—')
                  : campo.campo === 'sexo' && texto === null
                    ? SEXOS.INDEFINIDO
                    : (texto ?? '—')}
              </span>
              {deOndeVem && (
                <Badge variant="outline" className="text-xs font-normal shrink-0">
                  {deOndeVem}
                </Badge>
              )}
            </div>
          );
        })}
      </div>

      <Alert>
        <AlertDescription>
          Passam de &quot;{previa.absorvido.nome}&quot; para &quot;{previa.mantido.nome}&quot;:{' '}
          <strong>{resumirTotais(previa.totais)}</strong>. Favoritos, tags e grupos de estudo
          também. Os próximos exames do laboratório com os dados de &quot;{previa.absorvido.nome}
          &quot; vão direto para &quot;{previa.mantido.nome}&quot;.
        </AlertDescription>
      </Alert>
    </div>
  );
}
